/* eslint-disable react/jsx-pascal-case */
import { lazy } from "react";
import { createBrowserRouter, useParams } from "react-router-dom";

import { PublicRoute, PublicURL } from "./routes/PublicRoute";
import { PrivateRoute } from "./routes/PrivateRoute";
import { AdminRoute } from "./routes/AdminRoute";
import { TaskProvider } from "./Components/TasksManagement/TaskContext";
import { SignUpProvider } from "./context/SignUpContext";
import { DashboardProvider } from "./context/DashboardContext";

// Layout (always visible — never lazy-load)
import { Connect } from "./Components/Connect/Connect";

// ---- Lazy-loaded route-level components ----

// Auth / public flows
const Login = lazy(() => import("./Components/Initial/Login"));
const ForgotPassword = lazy(() => import("./Components/Initial/ForgotPassword"));
const SignupMain = lazy(() => import("./Components/Initial/SignUpComponent/SignupMain"));

// Connect (chat) sections
const ConversationSection = lazy(() =>
  import("./Components/Connect/ConversationSection").then((m) => ({
    default: m.ConversationSection,
  }))
);
const ReplySection = lazy(() =>
  import("./Components/Connect/ReplySection").then((m) => ({
    default: m.ReplySection,
  }))
);
const FileUpload = lazy(() =>
  import("./Components/Connect/FileUpload").then((m) => ({
    default: m.FileUpload,
  }))
);
const PrivateMsg = lazy(() =>
  import("./Components/Connect/PrivateMsg").then((m) => ({
    default: m.PrivateMsg,
  }))
);
const UserProfile = lazy(() => import("./Components/Connect/UserProfile"));
const MuteNotifications = lazy(() => import("./Components/Connect/MuteNotifications"));
const MultiCompanySwitcher = lazy(() => import("./Components/Connect/MultiCompanySwitcher"));
const Notifications = lazy(() => import("./Components/Connect/Notifications"));
const FileHubUrl = lazy(() => import("./Components/Connect/FileHubUrl"));
const PublicFileViewer = lazy(() => import("./Components/Connect/PublicFileViewer"));

// File hub & rooms
const FileHub = lazy(() => import("./Components/FileHub/FileHubs"));
const CreateRoom = lazy(() => import("./Components/Room/CreateRoom"));
const UpdateRoom = lazy(() => import("./Components/Room/UpdateRoom"));

// Admin
const TeamManageHome = lazy(() => import("./Components/Admin/TeamManagement/TeamManageHome"));
const RoomCategoryHome = lazy(() => import("./Components/Admin/RoomCategory/RoomCategoryHome"));
const SharedTeamTagsHome = lazy(() => import("./Components/Admin/SharedTeamTags/SharedTeamTagsHome"));
const MargeCompany = lazy(() => import("./Components/Admin/MergeCompany/MargeCompany"));
const RolebackCompany = lazy(() => import("./Components/Admin/MergeCompany/RolebackCompany"));
const ModuleManagementHome = lazy(() => import("./Components/Admin/ModuleManagement/ModuleManagementhome"));
const RoleManagementHome = lazy(() => import("./Components/Admin/RoleManagement/RoleManagementHome"));
const VenueManager = lazy(() => import("./Components/Admin/VenueManagement/VenueManager"));
const AccountSetting = lazy(() => import("./Components/Admin/AccountSetting/AccountSetting"));
const OtpManagement = lazy(() => import("./Components/Admin/OtpManagement/OtpManagement"));
const OtpHistory = lazy(() => import("./Components/Admin/OtpHistory/OtpHistory"));
const BusinessManager = lazy(() => import("./Components/Admin/BusinessAccount/BusinessManager"));
const UserManagementHome = lazy(() => import("./Components/Admin/UserManagement/UserManagementHome"));
const SubscriptionBillingHome = lazy(() => import("./Components/Admin/SubscriptionBilling/SubscriptionBillingHome"));
const SubscriptionManagement = lazy(() => import("./Components/Admin/SubscriptionManagement/SubscriptionManagement"));
const BusinessAccountInformation = lazy(() => import("./Components/Admin/CompanyInformation/BusinessAccountInformation"));
const ModulesManagementDashboard = lazy(() => import("./Components/Admin/ModulesManagement/ModulesManagementDashboard"));
const TaskSettings = lazy(() => import("./Components/Admin/TaskSettings/TaskSettings"));

// Tasks
const TaskManagementHome = lazy(() => import("./Components/TasksManagement/TaskManagementHome"));
const CreateQuickTask = lazy(() => import("./Components/TasksManagement/CreateTaskTab/CreateQuickTask"));
const TaskPropertiesCon = lazy(() => import("./Components/TasksManagement/TaskProperties/TaskPropertiesCon"));

// Calendar / calls / maintenance
const CalendarHome = lazy(() => import("./Components/Calendar/CalendarHome"));
const Calling_meeting = lazy(() => import("./Components/Call_window/Calling_meeting"));
const MaintenanceHome = lazy(() => import("./Components/Maintenance/MaintenanceHome"));

// EOD / sales
const EodReportHome = lazy(() => import("./Components/EodReport/EodReportHome"));
const SalesReportHome = lazy(() => import("./Components/EodSales/SalesReportHome"));
const CreateAndEditReport = lazy(() => import("./Components/EodReport/CreateEdit/CreateAndEditReport"));

// AI / DB
const WorkFreeliAI = lazy(() => import("./Components/Ai/WorkFreeliAI"));
const QueryAssistant = lazy(() => import("./Components/Ai/QueryAssistant"));
const OpenWebUIAssistant = lazy(() => import("./Components/Ai/OpenWebUIAssistant"));
const OpenWAAssistant = lazy(() => import("./Components/Ai/OpenWAAssistant"));
const WorkFreeliDB = lazy(() => import("./Components/MindsDB/WorkFreeliDB"));

// Global popups
const ProductPopup = lazy(() => import("./Components/GlobalPopups/ProductPopup"));
const ProductPopupMain = lazy(() => import("./Components/GlobalPopups/ProductPopupMain"));

// Dashboard
const WorkfreeliDashboard = lazy(() => import("./Components/Dashboard/WorkfreeliDashboard"));
const FlaggedItems = lazy(() => import("./Components/Dashboard/partials/flaggedItems/FlaggedItems"));
const FavouritedfilesLists = lazy(() => import("./Components/Dashboard/update_partials/myFavoriteFiles/Filesitems/FavouritedfilesLists"));

// Theme
const ThemeCustomizer = lazy(() => import("./Components/themeCustomize/ThemeCustomizer"));

const PublicFileViewerWrapper = () => {
  const { url } = useParams();
  console.log("PublicFileViewerWrapper url: ", url);
  // decode url if needed
  return <PublicFileViewer url={decodeURIComponent(url)} />;
};

export const ComponentProvier = createBrowserRouter([
  {
    path: "/",
    loader: PrivateRoute,
    element: <Connect />,
    children: [
      {
        path: "connect",
        children: [
          {
            path: ":conversation_id",
            element: <ConversationSection/>,
              handle: { root: "conversation" },
            children: [
              {
                path: "filter/:filter_name",
                element: <></>,
                children: [
                  {
                    path: ":search_string",
                    element: <></>,
                  },
                ],
              },
              {
                path: "private_update/:msg_id",
                element: <PrivateMsg />,
              },
              {
                path: "file_hub",
                element: <FileHub />,
                children: [
                  {
                    path: ":file_hub_tab",
                    element: <></>,
                  },
                ],
              },
              {
                path: "daily-report",
                element: <EodReportHome />,
              },
              {
                path: "maintenance",
                element: <MaintenanceHome />,
              },
              {
                path: "create-reports",
                element: <CreateAndEditReport />,
              },
              {
                path: "edit-reports",
                element: <CreateAndEditReport />,
              },
              {
                path: "reply/:msg_id",
                element: <ReplySection />,
              },
              {
                path: "update_room",
                element: <UpdateRoom />,
                children: [
                  {
                    path: ":popup",
                    element: <></>,
                  },
                ],
              },
              {
                path: "file_upload_old",
                element: <FileUpload is_reply_msg={"no"} type="file_upload" />,
              },
              {
                path: "mute_conversation",
                element: <MuteNotifications />,
              },
              {
                path: "profile",
                element: <UserProfile />,
              },
              {
                path: "tasks",
                element: <TaskManagementHome from="room" />,
                children: [
                  {
                    path: ":tasks_tab",
                    element: <></>,
                    children: [
                      {
                        path: ":task_id",
                        element: <></>,
                      },
                    ],
                  },
                  {
                    path: "create-task",
                    element: (
                      <TaskProvider>
                        {" "}
                        <CreateQuickTask />
                      </TaskProvider>
                    ),
                  },
                ],
              },
              {
                path: ":task_id",
                element: (
                  <TaskProvider>
                    {" "}
                    <TaskPropertiesCon />
                  </TaskProvider>
                ),
              },
            ],
          },
          {
            path: "dashboard",
            element: (
              <DashboardProvider>
                {" "}
                <WorkfreeliDashboard />
              </DashboardProvider>
            ),
            children: [
              {
                path: ":flagged_items_tab",
                element: (
                  // <TaskProvider>
                    <FlaggedItems />
                  // </TaskProvider>
              ),
              },
            ],

          },
          {
            path: "flagged-items/:id/:item_type",
            element: (<TaskProvider> <FlaggedItems /> </TaskProvider> ),
            children: [
              {
                path: ":task_id",
                element: (
                  <TaskProvider>
                    <TaskPropertiesCon />
                  </TaskProvider>
                ),
              },
              {
                path: "reply/:msg_id",
                element: (
                  <TaskProvider>
                    <ReplySection />
                  </TaskProvider>
                ),
              },
            ],
          },
          {
            path: "favorited_file/:id/:item_type",
            element: <FavouritedfilesLists />,

          },
          {
            path: "tasks",
            element: <TaskManagementHome />,
            children: [
              {
                path: ":tasks_tab",
                element: <></>,
                children: [
                  {
                    path: ":task_id",
                    element: <TaskPropertiesCon />,
                  },
                ],
              },
              {
                path: "create-task",
                element: (
                  <TaskProvider>
                    {" "}
                    <CreateQuickTask />
                  </TaskProvider>
                ),
              },
            ],
          },
          {
            path: "reply/:msg_id",
            element: <ReplySection />,
            children: [
              {
                path: "file_upload",
                element: <FileUpload is_reply_msg={"yes"} type="file_upload" />,
              },
            ],
          },
          {
            path: "create_room",
            element: <CreateRoom />,
            children: [
              {
                path: ":popup",
                element: <></>,
              },
            ],
          },
          {
            path: "profile",
            element: <UserProfile />,
          },
          {
            path: "workfreeli-ai",
            element: <WorkFreeliAI />,
          },
          {
            path: "mcp",
            element: <QueryAssistant />,
          },
          {
            path: "ai-assistant",
            element: <OpenWebUIAssistant />,
          },
          {
            path: "open-wa",
            element: <OpenWAAssistant />,
          },
          {
            path: "workfreeli-db",
            element: <WorkFreeliDB />,
          },
          {
            path: "admin-settings",
            loader: AdminRoute,
            element: <UserManagementHome />,
          },
          {
            path: "s-loader",
            // element: <UserManagementListSkeleton />,
          },
          {
            path: "notifications",
            element: <Notifications />,
          },
          {
            path: "team-management",
            loader: AdminRoute,
            element: <TeamManageHome />,
          },
          {
            path: "shared-team-tags",
            loader: AdminRoute,
            element: <SharedTeamTagsHome />,
          },
          {
            path: "task-management",
            loader: AdminRoute,
            element: <TaskSettings />,
          },
          {
            path: "marge-company",
            element: <MargeCompany />,
          },
          {
            path: "role-manage",
            element: <RoleManagementHome />,
          },
          {
            path: "module-management",
            element: <ModuleManagementHome />,
          },
          {
            path: "rollback-company",
            element: <RolebackCompany />,
          },
          {
            path: "room-category",
            loader: AdminRoute,
            element: <RoomCategoryHome />,
          },
          {
            path: "calendar",
            element: <CalendarHome />,
          },
          {
            path: "switch-account",
            element: <MultiCompanySwitcher />,
          },
          {
            path: "file_hub",
            element: <FileHub />,
            children: [
              {
                path: ":file_hub_tab",
                element: <></>,
              },
            ],
          },
          {
            path: "daily-report",
            element: <EodReportHome />,
            children: [
              {
                path: "create-report",
                element: <CreateAndEditReport />,
              },
            ],
          },
          {
            path: "daily-sales",
            element: <SalesReportHome />,
            // children: [
            //   {
            //     path: "create-report",
            //     element: <CreateAndEditReport />,
            //   },
            // ],
          },
          {
            path: "maintenance",
            element: <MaintenanceHome />,
          },
          {
            path: "venue-management",
            loader: AdminRoute,
            element: <VenueManager />,
          },
          {
            path: "account-setting",
            loader: AdminRoute,
            element: <AccountSetting />,
          },
          {
            path: "otp-management",
            loader: AdminRoute,
            element: <OtpManagement />,
          },
          {
            path: "otp-history",
            loader: AdminRoute,
            element: <OtpHistory />,
          },
          {
            path: "business-management",
            loader: AdminRoute,
            element: <BusinessManager />,
          },
          // Old Company management component
          // {
          //   path: "company-management",
          //   loader: AdminRoute,
          //   element: <CompanyManagement />,
          // },

          // {
          //   path: "company-management",
          //   element: <CompanyManagement />,
          //   children: [
          //     {
          //       path: "management-account",
          //       element: <CompanyInformations />,
          //     },
          //   ],
          // },


          {
            path: "subscription-billing",
            loader: AdminRoute,
            element: <SubscriptionBillingHome />,
          },
          {
            path: "business-account-information",
            element: <BusinessAccountInformation />,
          },
          {
            path: "subscription-management/:section?",
            loader: AdminRoute,
            element: <SubscriptionManagement />,
          },
          {
            path: "modules-management-dashboard",
            loader: AdminRoute,
            element: <ModulesManagementDashboard />,
          },
          // {
          //   path: "subscription-management",
          //   loader: AdminRoute,
          //   element: (
          //     <SubscriptionAndBillingProvider>
          //       {" "}
          //       <SubscriptionAndBillingMain />
          //     </SubscriptionAndBillingProvider>
          //   ),
          // },

          {
            path: "theme-customizer",
            element: <ThemeCustomizer />,
          },
        ],
      },
    ],
    // errorElement: <ErrorPage />
  },
  {
    path: "/f/:url",
    loader: PublicURL,
    element: <PublicFileViewerWrapper />,
  },
  {
    path: "/pf/:url",
    loader: PublicRoute,
    element: <PublicFileViewerWrapper />,
  },
  {
    path: "/file",
    loader: PrivateRoute,
    element: <FileHubUrl />,
  },
  {
    path: "/login",
    loader: PublicRoute,
    element: <Login type="normal" />,
  },
  {
    path: "/signin_with_otp/:short_url",
    loader: PublicRoute,
    element: <Login type="otp_login" />,
  },
  {
    path: "/signin_with_otp",
    loader: PublicRoute,
    element: <Login type="otp_login" />,
  },
  {
    path: "/forgot-password",
    loader: PublicRoute,
    element: <ForgotPassword />,
  },
  {
    path: "/signup",
    loader: PublicRoute,
    element: (
      <SignUpProvider>
        <SignupMain type="normal" />
      </SignUpProvider>
    ),
  },
  {
    path: "/signup/:short_url",
    loader: PublicRoute,
    element: (
      <SignUpProvider>
        <SignupMain type="invitedUser" />
      </SignUpProvider>
    ),
  },
  // {
  //   path: "/signup/:company_id/:email",
  //   loader: PublicRoute,
  //   element: (
  //     <SignUpProvider>
  //       {" "}
  //       <SignupMain type="invitedUser" />
  //     </SignUpProvider>
  //   ),
  // },
  {
    path: "/productpage",
    element: <ProductPopup />,
  },
  {
    path: "/productpage_manipulated",
    element: <ProductPopupMain />,
  },
  {
    path: "/call/:cid",
    loader: PrivateRoute,
    element: <Calling_meeting />,
  },
  {
    path: "/c/:id",
    loader: PublicURL,
    element: <Calling_meeting />,
  },
  {
    path: "/u/:id",
    loader: PublicURL,
    element: <Calling_meeting />,
  },
]);
